import './AssetItem.css';

import React, { memo } from 'react'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Card, Col, Row } from 'react-bootstrap';
import UserItem from '../common/UserItem';
import { formatDate, setObjectType } from '../../utils';
import AssetRibbon from '../common/AssetRibbon';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaModel } from '../../models/BdaModel';
import { Tooltip } from 'antd';

type Props = {
  asset: BdaDataset | BdaModel;
  type: 'dataset' | 'model';
  actions?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onDownload?: React.MouseEventHandler<HTMLButtonElement>;
  onPreview?: React.MouseEventHandler<HTMLButtonElement>;
  onEdit?: React.MouseEventHandler<HTMLButtonElement>;
  onDelete?: React.MouseEventHandler<HTMLButtonElement>;
}

function AssetItem({ asset, type, actions, onClick, onDownload, onPreview, onEdit, onDelete }: Props) {
  const date = formatDate(asset.createDateTime!);
  const icon = type === 'dataset' ? solid('database') : solid('graduation-cap');

  const renderPills = () => {
    if (type === 'dataset') return (
      <Tooltip title="Data source">
        <div className="list-item-pill d-inline-block text-truncate">
          <FontAwesomeIcon icon={solid('database')} />
          <span className="ms-2">{asset.datasource?.name}</span>
        </div>
      </Tooltip>
    );

    return (
      <Tooltip title="Training Dataset">
        <div className="list-item-pill d-inline-block text-truncate">
          <FontAwesomeIcon icon={solid('table')} />
          <span className="ms-2">{(asset as BdaModel).trainingDataset?.name}</span>
        </div>
      </Tooltip>
    );
  }

  return (
    <Card className="app-list-item" onClick={onClick}>
      <Card.Header className={"list-item-header bg-" + type}>
        <div className="list-item-icon">
          <FontAwesomeIcon icon={icon} />
        </div>

        <Row className="gx-0 align-items-start">
          <Col className="list-item-date">
            <b>Created</b>&nbsp;
            <span>{date}</span>
          </Col>

          <Badge className="shadow w-auto">{asset.accessLevel}</Badge>
        </Row>

        <div className="header-content">
          <Tooltip title={asset.name} mouseEnterDelay={0.3}>
            <h5 className="list-item-title">{asset.name}</h5>
          </Tooltip>
          {/* <Badge bg="none" className={classNames('asset-mode', 'shadow', modeClass)} pill={true}>
            <FontAwesomeIcon icon={modeIcon} />
            <span className="ms-2">{asset.workflowMode}</span>
          </Badge> */}
          <p className="list-item-description line-clamp-2 mt-2">{asset.description}</p>
        </div>

        <Row className="gx-0 justify-content-center">
          {actions || (
            <Col className="text-center" xs={8}>
              {asset.created && (
                <>
                  {type === "model" && (
                    <Tooltip title="Download" mouseEnterDelay={0.3}>
                      <button className="circle-button" onClick={onDownload}>
                        <FontAwesomeIcon size="sm" icon={solid('download')} />
                      </button>
                    </Tooltip>
                  )}
                  {type === "dataset" && setObjectType<BdaDataset>(asset) && (
                    <>
                      {asset.datasetFileType!.type === "table" && (
                        <Tooltip title="Preview" mouseEnterDelay={0.3}>
                          <button className="circle-button" onClick={onPreview}>
                            <FontAwesomeIcon size="sm" icon={solid('eye')} />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit" mouseEnterDelay={0.3}>
                        <button className="circle-button ms-2" onClick={onEdit}>
                          <FontAwesomeIcon size="sm" icon={solid('pen')} />
                        </button>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Delete" mouseEnterDelay={0.3}>
                    <button className="circle-button ms-2" onClick={onDelete}>
                      <FontAwesomeIcon size="sm" icon={solid('trash')} />
                    </button>
                  </Tooltip>
                </>
              )}
              {!asset.created && (
                <button className="circle-button opacity-0">
                  <FontAwesomeIcon size="sm" icon={solid('times')} />
                </button>
              )}
            </Col>
          )}
        </Row>
      </Card.Header>
      <Card.Body className="p-2 position-relative">
        <div className="asset-ribbons">
          {(asset.createdByServiceId === null) && (
            <AssetRibbon
              icon={solid('user')}
              color="info"
              tooltip="This has been created by a user."
            />
          )}
          {(asset.createdByServiceId !== null) && (
            <AssetRibbon
              icon={solid('cogs')}
              color="dark"
              tooltip="This is an output from an execution."
            />
          )}
          {!asset.created && (
            <AssetRibbon
              icon={solid('lock')}
              color="warning"
              tooltip="This has not been created yet."
            />
          )}
        </div>

        <UserItem user={asset.owner!} organization={asset.organization!} className="mb-2" />

        <div className="tags-wrapper mb-2">
          {asset.tags.length === 0 && <Badge className="bg-secondary" pill={true}>0 tags</Badge>}
          {asset.tags.map((tag) => (
            <Badge key={tag} className="ms-1" pill={true}>
              <FontAwesomeIcon icon={solid('tag')} />
              <span className="ms-2">{tag}</span>
            </Badge>
          ))}
        </div>

        <div className="text-center">{renderPills()}</div>
      </Card.Body>
    </Card >
  )
}

export default memo(AssetItem);
